import { FC, useMemo } from "react";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Box, Button, Grid, Stack, Tab } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { NavigateIcon } from "../../../assets/icons";
import { AppMenuKey, AppMenuSectionItem, AppRoutes } from "../../../common/routesUtils";
import DropdownSelect from "../../../components/common/DropdownSelect";
import ForbiddenTooltip from "../../../components/common/ForbiddenTooltip";
import { TransportPerspectiveSelector } from "../../../components/common/TransportPerspectiveSelector";
import { View } from "../../../components/layout/View";
import { PageType } from "../../../config/page.types";
import { useBreadcrumbs } from "../../../context/BreadcrumbsProvider";
import { useFiltersRegistry } from "../../../context/FiltersProvider";
import { usePageConfigurationState } from "../../../context/PageConfigrations/usePageConfigurationState";
import { EmissionsTransportResultsDataDTO } from "../../../datasources/api-client/data-contracts";
import { useNavigationPreferences } from "../../../hooks/usePreferences";
import { usePermissions } from "../../../permissions/usePermissions";
import Greenrouter from "./tabs/Greenrouter";
import Overview from "./tabs/Overview";
import ThirdParty from "./tabs/ThirdParty";
import { useTransportData } from "./useTransportData";

interface Props {
	menuKeys?: Array<AppMenuKey>;
	path?: Array<AppMenuSectionItem>;
}

export type EmissionDataKey = keyof EmissionsTransportResultsDataDTO;
// Keys for the dropdown select
type DropdownEmissionsKey = "co2Ttw" | "co2Wtw";

const ResultsTransport: FC<Props> = () => {
	const { t } = useTranslation(["result.transport", "common"]);

	const navigate = useNavigate();
	const { visitPage } = useBreadcrumbs();
	const { navigationPreferences } = useNavigationPreferences();

	const { appliedFilters } = useFiltersRegistry();

	// Page configuration, stored in localStorage by PageType
	const [config, setConfig] = usePageConfigurationState(PageType.ResultsTransport, {
		emissionsKey: "co2Ttw",
		tabValue: "overview",
		overviewEmissionsIntervalsPeriodType: "month",
		overviewIntervalMode: "emissions",
		greenrouterEmissionsMapSource: "trips-origin",
	});

	// Dropdown options for emission selection
	const dropdownSelectEmissions = [
		{ key: "co2Ttw", label: t("common:ttw select") },
		{ key: "co2Wtw", label: t("common:wtw select") },
	];

	// Update selected emission type in page config
	const handleEmissionChange = (key: string) => {
		setConfig({ emissionsKey: key as DropdownEmissionsKey });
	};

	const handleChange = (event: React.SyntheticEvent, newValue: "overview" | "greenrouter" | "thirdparty") => {
		setConfig({ tabValue: newValue });
	};

	/**
	 * Payload for data fetching
	 * Automatically re-memoized when filters or period change
	 */
	const payload = useMemo(() => ({ ...appliedFilters.payload }), [appliedFilters]);

	const { emissions, thirdParty, fileDownload } = useTransportData({
		payload,
		periodType: config.overviewEmissionsIntervalsPeriodType,
		emissionsMapSource: config.greenrouterEmissionsMapSource,
		perspective: navigationPreferences.transportsPerspective,
		tab: config.tabValue,
	});

	const { canAccessPage } = usePermissions();

	// The GreenRouter list shows transports or entities depending on the perspective (same route).
	const isGreenRouterListForbidden =
		!canAccessPage(PageType.ResultsTransportGreenRouter) &&
		!canAccessPage(PageType.ResultsTransportGreenRouterEntities);
	const isThirdPartyListForbidden = !canAccessPage(PageType.ResultsTransportThirdParty);

	return (
		<View
			pageType={PageType.ResultsTransport}
			headerContext={{
				payload: { ...payload, perspective: navigationPreferences.transportsPerspective },
				fileDownload,
				t,
			}}
		>
			<TabContext value={config.tabValue}>
				<Grid container spacing={2}>
					<Grid size={12}>
						<Stack direction={"row"} spacing={2} justifyContent={"space-between"} alignItems={"center"}>
							<Stack direction={"row"} spacing={2}>
								<TabList onChange={handleChange} aria-label="Transport tabs">
									<Tab
										label={t("common:overview")}
										value="overview"
										sx={{ textTransform: "uppercase" }}
									/>
									<Tab
										label={t("common:greenrouter")}
										value="greenrouter"
										sx={{ textTransform: "uppercase" }}
									/>
									<Tab
										label={t("third parties")}
										value="thirdparty"
										sx={{ textTransform: "uppercase" }}
									/>
								</TabList>

								{config.tabValue === "greenrouter" && (
									<ForbiddenTooltip forbidden={isGreenRouterListForbidden}>
										<Button
											variant="contained"
											color="primary"
											disabled={isGreenRouterListForbidden}
											onClick={() => {
												visitPage(PageType.ResultsTransportGreenRouter);
												navigate(AppRoutes.ResultsTransportGreenrouter);
											}}
											endIcon={<NavigateIcon color="inherit" />}
										>
											{t("view greenrouter transport")}
										</Button>
									</ForbiddenTooltip>
								)}

								{config.tabValue === "thirdparty" && (
									<ForbiddenTooltip forbidden={isThirdPartyListForbidden}>
										<Button
											variant="contained"
											color="primary"
											disabled={isThirdPartyListForbidden}
											onClick={() => {
												visitPage(PageType.ResultsTransportThirdParty);
												navigate(AppRoutes.ResultsTransportThirdParty);
											}}
											endIcon={<NavigateIcon color="inherit" />}
										>
											{t("view third party transport")}
										</Button>
									</ForbiddenTooltip>
								)}
							</Stack>

							<Box sx={{ flexGrow: 1 }} />
							<DropdownSelect
								text={t("display for")}
								value={config.emissionsKey}
								loading={emissions.transport.isLoading}
								onChange={handleEmissionChange}
								dropdownItems={dropdownSelectEmissions}
								variant="standard"
							/>

							<TransportPerspectiveSelector />
						</Stack>
					</Grid>
					<TabPanel value="overview" sx={{ padding: 0, width: "100%" }}>
						<Overview
							transportEmissions={emissions.transport}
							keyEmissions={config.emissionsKey}
							emissionsIntervals={emissions.intervals}
							emissionsIntervalsPeriodType={config.overviewEmissionsIntervalsPeriodType}
							setEmissionsIntervalsPeriodType={(periodType) =>
								setConfig({ overviewEmissionsIntervalsPeriodType: periodType })
							}
							intervalMode={config.overviewIntervalMode}
							onIntervalModeChange={(intervalMode) => setConfig({ overviewIntervalMode: intervalMode })}
						/>
					</TabPanel>

					<TabPanel value="greenrouter" sx={{ padding: 0, width: "100%" }}>
						<Greenrouter
							transportEmissions={emissions.transport}
							keyEmissions={config.emissionsKey}
							emissionTypeTransport={emissions.typeTransport}
							emissionsChainTransport={emissions.chainTransport}
							emissionsMap={emissions.emissionsMap}
							selectedSource={config.greenrouterEmissionsMapSource}
							onSourceChange={(value) => setConfig({ greenrouterEmissionsMapSource: value })}
						/>
					</TabPanel>

					<TabPanel value="thirdparty" sx={{ padding: 0, width: "100%" }}>
						<ThirdParty
							keyEmissions={config.emissionsKey}
							transportEmissions={emissions.transport}
							calculationMethods={thirdParty.calculationMethods}
							nodesCertification={thirdParty.nodesCertification}
							environmentalKpi={thirdParty.environmentalKpi}
							typeTransport={thirdParty.typeTransport}
							chainsTransport={thirdParty.chainTransport}
						/>
					</TabPanel>
				</Grid>
			</TabContext>
		</View>
	);
};

export default ResultsTransport;
